import React, { useRef, useState } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { FreeMode, Navigation, Pagination } from 'swiper/modules';
import { getVisibleSlides } from './funcions';
import { useScript } from 'hooks/useScript';
import { getPropsSwiper } from './breakpoints';
import Icona from 'components/3catinfo/ui/Icona';
import Titol from 'components/3catinfo/Titol';
import Text from 'components/3catinfo/Text';
import Boto from 'components/3catinfo/Boto';
import __isUndefined from 'lodash/isUndefined';
import __isEmpty from 'lodash/isEmpty';
import clsx from 'clsx';
import 'swiper/css';
import 'swiper/css/free-mode';
import 'swiper/css/grid';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import styles from './slider.module.scss';

const MoreItemsButton = (props) => {
    const { mostrarBotoMesItems = true, propsEnllac = {}, textBotoMes = 'Mostra-ho tot' } = props;
    if (!mostrarBotoMesItems || __isEmpty(propsEnllac)) return <></>;

    const defaultPropsEnllac = {
        posicio: 'dreta',
        icona: 'fletxa_simple_dreta',
        iconaInline: true
    };

    const finalPropsEnllac = { ...defaultPropsEnllac, ...propsEnllac };
    const { slug, slug2, linkTipus, href, posicio, className, icona } = finalPropsEnllac;

    const stylesBoto = clsx(styles.btnMoreItemsContent, posicio && styles[posicio], className);

    return (
        <div className={stylesBoto}>
            <Boto
                text={textBotoMes}
                estil='transparent'
                mida='s'
                tema='alignDreta'
                icona={icona}
                linkSlug={slug}
                slug2={slug2}
                type={linkTipus}
                href={href}
                dataTestId={`boto-tots`}
            />
        </div>
    );
};

const SliderNoJavascript = (props) => {
    const { items, estilModul, className } = props;
    const stylesSliderNoJavascript = clsx(
        styles.sliderNoJavascript,
        styles.sliderDefault,
        estilModul && styles[`${estilModul}-nojavascript`],
        className
    );
    return (
        <div className={styles.infoTitol}>
            <div className={stylesSliderNoJavascript}>
                {items.map((component, index) => {
                    const generateUniqueId = () => `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
                    const key = component.key || generateUniqueId();
                    return (
                        <div key={`slider-no-javascript-${key}-${index}`} className={styles.slideContentNoJavascript}>
                            {component}
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

const Slider = (props) => {
    const {
        titol = '',
        fletxes = 'default',
        estilFletxes,
        tag = 'h2',
        noTitol = false,
        tema,
        titolClass,
        descripcio,
        format,
        className,
        estil,
        estilModul,
        onSwiper
    } = props;
    const items = props.children;
    const navigationPrev = useRef(null);
    const navigationNext = useRef(null);
    const paginationRef = useRef(null);
    const swiperRef = useRef(null);
    const [swiperInit, setSwiperInit] = useState(false);
    const [swiperMoved, setSwiperMoved] = useState(false);
    const [sliderPosition, setSliderPosition] = useState(0);
    const [visibleSlides, setVisibleSlides] = useState(getVisibleSlides(getPropsSwiper(format)));
    const [totalPages, setTotalPages] = useState(0);
    const { isJavaScriptClientEnabled } = useScript();

    const getInitialSlidesPerView = () => {
        if (typeof window === 'undefined') return 1;
        const propsSwiper = getPropsSwiper(format);
        return getVisibleSlides(propsSwiper) / 2;
    };
    const [currentSlidesPerView, setCurrentSlidesPerView] = useState(getInitialSlidesPerView);

    const sliderId = 'id' + ((tema || '') + titol + format).replace(/^[^a-z]+|[^\w.-]+/gi, '');
    const wrapperTag = visibleSlides <= 2 ? 'div' : 'ul';
    const slideTag = visibleSlides <= 2 ? 'div' : 'li';
    const swiperRenderKey = `${sliderId}-${wrapperTag}-${slideTag}`;
    let resizeTimeOut;

    if (__isUndefined(props.children) || props.children.length <= 0) return <></>;

    const stylesSwiper = clsx(
        styles.innerSlider,
        estil && styles[estil],
        sliderPosition === 1 && styles.dreta,
        sliderPosition === 0 && styles.esquerra,
        className
    );

    const stylesModul = clsx(
        styles.slider,
        estilModul && styles[estilModul],
        swiperInit && styles.init,
        __isEmpty(titol) && fletxes !== 'mig' && styles.noTitol,
        !__isEmpty(descripcio) && styles.siDescripcio
    );

    const stylesFletxes = clsx(
        styles.contentFletxes,
        totalPages > 1 && styles.visible,
        fletxes && styles[`fletxes-${fletxes}`],
        estilFletxes && styles[estilFletxes]
    );

    const stylesIconaFletxa = clsx(styles.iconaFletxa, fletxes === 'mig' && styles.fletxaMig);

    const _buildSlide = (component, i) => {
        const generateUniqueId = () => `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
        const key = component.key || generateUniqueId();

        // Passa el currentSlidesPerView al component si és possible
        const componentWithColumnsRow = React.isValidElement(component)
            ? React.cloneElement(component, { numColumnsRow: Math.floor(currentSlidesPerView) })
            : component;

        return (
            <SwiperSlide tag={slideTag} key={`slider-${key}`} virtualIndex={i} data-testid={`slider-${key}`}>
                {componentWithColumnsRow}
            </SwiperSlide>
        );
    };

    const _iterateItems = (items) => {
        if (!swiperMoved) {
            let sliderItems = !__isEmpty(items)
                ? Array.from(items)
                      .slice(0, visibleSlides)
                      .map((component, i) => _buildSlide(component, i))
                : null;
            if (sliderItems?.length < items.length) {
                for (let i = 0; i < items.length - visibleSlides; i++) {
                    sliderItems = sliderItems.concat(
                        <SwiperSlide
                            tag={slideTag}
                            key={`slider-${i}`}
                            virtualIndex={i}
                            data-testid={`slider-${i}`}
                            aria-hidden='true'
                            //onKeyDown={(event) => handleKeyDown(event, i + 1)}
                        />
                    );
                }
            }

            return sliderItems;
        } else {
            return !__isEmpty(items) ? Array.from(items).map((component, i) => _buildSlide(component, i)) : null;
        }
    };

    const _renderSwiper = () => {
        const propsSwiper = getPropsSwiper(format);
        const BUTTON_ROLE = 'button, [role="button"]';

        const _slidesIndexOf = (swiper) => {
            // ✅ Verificar que visibleSlidesIndexes existeix i és un array
            if (
                !swiper.visibleSlidesIndexes ||
                !Array.isArray(swiper.visibleSlidesIndexes) ||
                swiper.visibleSlidesIndexes.length === 0
            ) {
                return; // Sortir si no hi ha dades vàlides
            }

            const slidesInSight = swiper.slides.filter((slide) => slide.classList.contains('swiper-slide-visible'));
            const lastVisibleSlideIndex = swiper.visibleSlidesIndexes[swiper.visibleSlidesIndexes?.length - 1];
            const lastVisibleSlidePosition = swiper.slidesGrid[lastVisibleSlideIndex];
            const lastVisibleSlideWidth = swiper.slidesSizesGrid[lastVisibleSlideIndex];
            const lastVisibleSlideEnd = lastVisibleSlidePosition + lastVisibleSlideWidth;
            const isLastSlideOverFlowing = swiper.size - swiper.translate < lastVisibleSlideEnd;
            const firstVisibleSlideIndex = swiper.visibleSlidesIndexes[0];
            const firstVisibleSlidePosition = swiper.slidesGrid[firstVisibleSlideIndex];
            const isFirstSlideOverFlowing = swiper.translate + firstVisibleSlidePosition < 0;

            slidesInSight.forEach((slide) => {
                if (slide.querySelectorAll('li').length > 0) {
                    slide.querySelectorAll('li').forEach((li) => {
                        if (slidesInSight.length === 2 && isFirstSlideOverFlowing && isLastSlideOverFlowing) {
                            if (slide === slidesInSight[0]) {
                                li.setAttribute('aria-hidden', 'false');
                            }
                        } else {
                            if (
                                !isLastSlideOverFlowing &&
                                slide === !slidesInSight[slidesInSight.length - 1] &&
                                !isFirstSlideOverFlowing &&
                                slide === !slidesInSight[0]
                            ) {
                                li.setAttribute('aria-hidden', 'false');
                            }
                        }
                    });
                }
                if (slide.querySelectorAll('a').length > 0) {
                    slide.querySelectorAll('a').forEach((link) => {
                        if (slidesInSight.length === 2 && isFirstSlideOverFlowing && isLastSlideOverFlowing) {
                            if (slide === slidesInSight[0]) {
                                link.setAttribute('tabindex', '0');
                                slide.setAttribute('aria-hidden', 'false');
                            } else {
                                link.setAttribute('tabindex', '-1');
                                slide.setAttribute('aria-hidden', 'true');
                            }
                        } else {
                            if (isLastSlideOverFlowing && slide === slidesInSight[slidesInSight.length - 1]) {
                                link.setAttribute('tabindex', '-1');
                                slide.setAttribute('aria-hidden', 'true');
                            } else if (isFirstSlideOverFlowing && slide === slidesInSight[0]) {
                                link.setAttribute('tabindex', '-1');
                                slide.setAttribute('aria-hidden', 'true');
                            } else {
                                link.setAttribute('tabindex', '0');
                                slide.setAttribute('aria-hidden', 'false');
                            }
                        }
                    });
                }
                if (slide.querySelectorAll(BUTTON_ROLE).length > 0) {
                    slide.querySelectorAll(BUTTON_ROLE).forEach((button) => {
                        if (slidesInSight.length === 2 && isFirstSlideOverFlowing && isLastSlideOverFlowing) {
                            if (slide === slidesInSight[0]) {
                                button.setAttribute('tabindex', '0');
                                slide.setAttribute('aria-hidden', 'false');
                            } else {
                                button.setAttribute('tabindex', '-1');
                                slide.setAttribute('aria-hidden', 'true');
                            }
                        } else {
                            if (isLastSlideOverFlowing && slide === slidesInSight[slidesInSight.length - 1]) {
                                button.setAttribute('tabindex', '-1');
                                slide.setAttribute('aria-hidden', 'true');
                            } else if (isFirstSlideOverFlowing && slide === slidesInSight[0]) {
                                button.setAttribute('tabindex', '-1');
                                slide.setAttribute('aria-hidden', 'true');
                            } else {
                                button.setAttribute('tabindex', '0');
                                slide.setAttribute('aria-hidden', 'false');
                            }
                        }
                    });
                }
            });
            swiper.slides.forEach((slide) => {
                if (!slide.classList.contains('swiper-slide-visible')) {
                    if (slide.querySelectorAll('a').length > 0) {
                        slide.querySelectorAll('a').forEach((link) => {
                            link.setAttribute('tabindex', '-1');
                            slide.setAttribute('aria-hidden', 'true');
                        });
                    }
                    if (slide.querySelectorAll(BUTTON_ROLE).length > 0) {
                        slide.querySelectorAll(BUTTON_ROLE).forEach((button) => {
                            button.setAttribute('tabindex', '-1');
                            slide.setAttribute('aria-hidden', 'true');
                        });
                    }
                }
            });
        };

        return (
            <>
                <div className={styles.contSlider} aria-live='polite'>
                    <Swiper
                        key={swiperRenderKey}
                        {...propsSwiper}
                        wrapperTag={wrapperTag}
                        pagination={{
                            el: paginationRef.current,
                            type: 'fraction',
                            renderFraction: (currentClass, totalClass) => {
                                return `<span class="${currentClass}"></span> de <span class="${totalClass}"></span>`;
                            }
                        }}
                        ref={swiperRef}
                        navigation={{
                            prevEl: navigationPrev.current,
                            nextEl: navigationNext.current
                        }}
                        onBeforeInit={(swiper) => {
                            if (swiper.params?.navigation) {
                                swiper.params.navigation.prevEl = navigationPrev.current;
                                swiper.params.navigation.nextEl = navigationNext.current;
                            }
                            setVisibleSlides(getVisibleSlides(propsSwiper));
                        }}
                        onAfterInit={(swiper) => {
                            setSwiperInit(true);
                            _slidesIndexOf(swiper);
                        }}
                        modules={[Navigation, FreeMode, Pagination]}
                        className={stylesSwiper}
                        updateOnWindowResize={true}
                        onProgress={(swiper) => {
                            setSliderPosition(swiper.progress);
                            _slidesIndexOf(swiper);
                        }}
                        onSwiper={(swiper) => {
                            setTimeout(() => {
                                if (swiper && swiper.params && swiper.params.slidesPerView) {
                                    setTotalPages(Math.ceil(swiper.slides.length / swiper.params.slidesPerView));
                                    // Actualitzar el currentSlidesPerView amb el valor actual
                                    setCurrentSlidesPerView(swiper.params.slidesPerView);
                                }
                            }, 100);
                            window.addEventListener('resize', function () {
                                clearTimeout(resizeTimeOut);
                                resizeTimeOut = setTimeout(() => {
                                    setVisibleSlides(getVisibleSlides(propsSwiper));
                                    if (swiper && swiper.params && swiper.params.slidesPerView) {
                                        setTotalPages(Math.ceil(swiper.slides.length / swiper.params.slidesPerView));
                                        // Actualitzar el currentSlidesPerView quan canvia la mida de la finestra
                                        setCurrentSlidesPerView(swiper.params.slidesPerView);
                                        swiper.update();
                                    }
                                }, 100);
                            });

                            if (onSwiper) onSwiper(swiper);
                        }}
                        onSlideChange={() => {
                            setSwiperMoved(true);
                        }}
                        data-testid={`slider-${sliderId}`}
                    >
                        {_iterateItems(items)}
                    </Swiper>
                </div>
                {/* <ul
                    ref={paginationRef}
                    className='swiper-pagination swiper-pagination-clickable swiper-pagination-bullets swiper-pagination-horizontal'
                ></ul> */}
                <div className={stylesFletxes}>
                    <button ref={navigationPrev} className={styles.anterior}>
                        <Icona
                            icona='fletxa_esquerra'
                            isBlanc={false}
                            alt='Anterior'
                            mida={fletxes === 'mig' ? 26 : 30}
                            inline={true}
                            className={stylesIconaFletxa}
                            tabIndex={0}
                        />
                    </button>
                    <div className={styles.index} ref={paginationRef}></div>
                    <button ref={navigationNext} className={styles.seguent}>
                        <Icona
                            icona='fletxa_dreta'
                            isBlanc={false}
                            alt='Següent'
                            mida={fletxes === 'mig' ? 26 : 30}
                            inline={true}
                            className={`${stylesIconaFletxa} ${styles.iconadreta}`}
                            tabIndex={0}
                        />
                    </button>
                </div>
            </>
        );
    };

    return !__isEmpty(items) ? (
        <section
            className={stylesModul}
            {...(!__isEmpty(titol) && !noTitol ? { 'aria-labelledby': sliderId } : {})}
            {...(!__isEmpty(titol) && noTitol ? { 'aria-label': titol } : {})}
        >
            {titol && !noTitol && (
                <div className={styles.infoTitol}>
                    <Titol titol={titol} sliderId={sliderId} tag={tag} estil={titolClass} />
                    {descripcio && <Text className={styles.descripcio} text={descripcio} tag='p' />}
                </div>
            )}

            <div className={styles.wrapperSlider}>
                {isJavaScriptClientEnabled ? (
                    _renderSwiper()
                ) : (
                    <SliderNoJavascript items={items} estilModul={estilModul} className={className} />
                )}
            </div>
            <MoreItemsButton {...props} />
        </section>
    ) : (
        <></>
    );
};

export default Slider;
